/**
 * The per-persona navigation shapes (workflow contract 3.5), rebuilt from the flows and
 * the shipped policy in wave W1 (coherence review section C, rulings 5 and 6).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * THE RULE (wave W1, Mahmoud's finding 2: reachability through menus only)
 *   A persona's menu carries every surface its day needs, read from the flow stations
 *   the class acts in and the policy rows the server grants it. The old note here said
 *   "an omitted entry never removes reachability"; in the interface it does, and that
 *   was the defect. The reachability gate (`reachability.test.ts`) now fails the build
 *   when a route a persona holds (a task it receives, a queue it works, a station the
 *   flows give it) cannot be reached from that persona's menu.
 *
 *   The server still decides every record: a menu entry is a door, never a permission.
 *
 * THE SEAM
 *   `/api/me` carries the server-derived `workflow_class`. The configuration capability
 *   probe remains only as the fallback for a null class.
 */

import { strings } from "../brand/strings";
import { navigationTree, PROGRAM_ROUTES, type NavEntry, type NavGroup } from "./navModel";
import { QUEUES } from "./queues";

/**
 * The menu shapes, keyed off the server's workflow class.
 * - `management` is the read-only class of ruling 5 (status and numbers, no data entry).
 *   The server does not answer it yet (no role carries it; see the W1 report), so the
 *   shape is ready for the day it does.
 * - `tpi` is the third party inspector of ruling 6.
 */
export type PersonaShape =
  "manager" | "management" | "qcLead" | "planner" | "inspector" | "contractor" | "tpi";

export const PERSONA_SHAPES: readonly PersonaShape[] = [
  "manager",
  "management",
  "qcLead",
  "planner",
  "inspector",
  "contractor",
  "tpi",
];

/**
 * What the client honestly knows about the principal. The server-derived workflow_class
 * (`/api/me`, M5 P2) selects the shape; the configuration probe stays as the fallback
 * for a payload that carries no class (older backend, or null = no class matched).
 */
export interface PersonaSignals {
  readonly configurationAllowed: boolean;
  readonly auditAllowed?: boolean | undefined;
  /** The server's coarse persona: qc_lead, manager, inspector, planner, contractor, tpi. */
  readonly workflowClass?: string | null;
}

/**
 * Resolve the shape. A workflow_class from the server wins; an unknown or absent class
 * falls back to the capability probes (manager vs contractor) as before.
 */
export function resolvePersonaShape(signals: PersonaSignals): PersonaShape {
  switch (signals.workflowClass) {
    case "qc_lead":
      return "qcLead";
    case "manager":
      return "manager";
    case "management":
      return "management";
    case "inspector":
      return "inspector";
    case "planner":
      return "planner";
    case "contractor":
      return "contractor";
    case "tpi":
      return "tpi";
    default:
      return signals.configurationAllowed || signals.auditAllowed === true
        ? "manager"
        : "contractor";
  }
}

/** The Tasks and inbox entry: every persona's day starts from it (LA W1.3). */
export const TASKS_ENTRY: NavEntry = { label: strings.nav.tasks, to: "/tasks" };

const PROGRAM_ENTRIES: readonly NavEntry[] = [
  { label: strings.nav.programEngineering, to: PROGRAM_ROUTES.engineering },
  { label: strings.nav.programProcurement, to: PROGRAM_ROUTES.procurement },
  { label: strings.nav.programConstruction, to: PROGRAM_ROUTES.construction },
  { label: strings.nav.programExternalAudits, to: PROGRAM_ROUTES.externalAudits },
];

/** The planning candidates view (R06): the unplanned work section of the calendar. */
export const PLANNING_CANDIDATES_ROUTE = "/calendar#unplanned-work";

const GUIDE: NavEntry = { label: strings.nav.guide, to: "/guide" };
/** Wave b12 frontend item 3: every persona reaches its own account and preferences. */
export const ACCOUNT_ENTRY: NavEntry = { label: strings.nav.account, to: "/account" };
const CONFIGURATION: NavEntry = {
  label: strings.nav.configuration,
  to: "/configuration",
  when: "configurationProbe",
};
const AUDIT: NavEntry = {
  label: strings.nav.audit,
  to: "/audit",
  when: "auditProbe",
};

/**
 * The contractor shape: MY work, in the domain loop's own order. NCR added in W1: the
 * contractor answers its non-conformities (F15 st 2-3, 7; img12 menu).
 */
export function contractorNavigation(): NavGroup[] {
  return [
    {
      heading: strings.nav.groups.myWork,
      entries: [
        TASKS_ENTRY,
        { label: strings.nav.myCalendar, to: "/calendar" },
        { label: strings.nav.myItps, to: "/itps" },
        // Wave W2: the contractor's own requests for attendance (R10).
        { label: strings.nav.myRfis, to: "/rfis" },
        { label: strings.nav.inspectionsOwn, to: "/inspections" },
        { label: strings.nav.findingsToAnswer, to: QUEUES.responseStep.route },
        { label: strings.nav.openFindings, to: QUEUES.openFindings.route },
        { label: strings.nav.yourNonConformities, to: "/actions" },
        { label: strings.nav.schedulesOwn, to: "/schedules" },
        { label: strings.nav.myEvaluation, to: "/evaluations" },
        // Wave CRED ruling 15: a contractor reads its own people's credentials.
        { label: strings.nav.credentials, to: "/credentials" },
      ],
    },
    { heading: strings.nav.groups.workspace, entries: [GUIDE, ACCOUNT_ENTRY] },
  ];
}

/**
 * The third party inspector (ruling 6): witness docket, own inspections, the findings it
 * raised, the calendar, the guide. No plan authoring, no "findings to answer", no
 * evaluation: the TPI is not the contractor.
 */
export function tpiNavigation(): NavGroup[] {
  return [
    {
      heading: strings.nav.groups.myWork,
      entries: [
        TASKS_ENTRY,
        { label: strings.nav.witnessDocket, to: "/docket" },
        { label: strings.nav.queueMyInspections, to: QUEUES.myInspections.route },
        { label: strings.nav.inspectionsOwn, to: "/inspections" },
        { label: strings.nav.findingsRaised, to: "/findings" },
        { label: strings.nav.calendar, to: "/calendar" },
      ],
    },
    { heading: strings.nav.groups.workspace, entries: [GUIDE, ACCOUNT_ENTRY] },
  ];
}

/**
 * The inspector: My Work first, then the program lenses read-only. NCR added in W1: the
 * inspector raises non-conformities (img67 Lead QC / QC Discipline Inspector lane).
 */
export function inspectorNavigation(): NavGroup[] {
  return [
    {
      heading: strings.nav.groups.myWork,
      entries: [
        TASKS_ENTRY,
        { label: strings.nav.queueMyInspections, to: QUEUES.myInspections.route },
        { label: strings.nav.queueRectifications, to: QUEUES.rectifications.route },
        { label: strings.nav.queueResponses, to: QUEUES.responsesToDecide.route },
        { label: strings.nav.calendar, to: "/calendar" },
        { label: strings.nav.rfis, to: "/rfis" },
      ],
    },
    {
      heading: strings.nav.groups.registers,
      entries: [
        { label: strings.nav.inspectionsRfis, to: "/inspections" },
        { label: strings.nav.myFindings, to: "/findings" },
        { label: strings.nav.nonConformities, to: "/actions" },
      ],
    },
    { heading: strings.nav.programsRead, entries: PROGRAM_ENTRIES },
    { heading: strings.nav.groups.workspace, entries: [GUIDE, ACCOUNT_ENTRY] },
  ];
}

/**
 * The QC lead: the queues first, each opening pre-filtered (R13); then the project she
 * approves (Projects, Parties), the calendar she owns (F03 img3 lane), the registers and
 * the programs.
 */
export function qcLeadNavigation(): NavGroup[] {
  return [
    {
      heading: strings.nav.groups.queues,
      entries: [
        TASKS_ENTRY,
        { label: strings.nav.queueItpReview, to: QUEUES.itpReview.route },
        { label: strings.nav.queueScheduleSign, to: QUEUES.scheduleSign.route },
        { label: strings.nav.queueResultApproval, to: QUEUES.resultApproval.route },
        { label: strings.nav.queueRectifications, to: QUEUES.rectifications.route },
      ],
    },
    {
      heading: strings.nav.groups.portfolio,
      entries: [
        { label: strings.nav.projectsContracts, to: "/portfolio/projects" },
        { label: strings.nav.partiesContractors, to: "/portfolio/parties" },
        { label: strings.nav.calendar, to: "/calendar" },
      ],
    },
    {
      heading: strings.nav.groups.registers,
      entries: [
        { label: strings.nav.itps, to: "/itps" },
        { label: strings.nav.schedules, to: "/schedules" },
        { label: strings.nav.rfis, to: "/rfis" },
        { label: strings.nav.inspectionsRfis, to: "/inspections" },
        { label: strings.nav.findingsPunch, to: "/findings" },
        { label: strings.nav.nonConformities, to: "/actions" },
        { label: strings.nav.standards, to: "/standards" },
        { label: strings.nav.credentials, to: "/credentials" },
      ],
    },
    { heading: strings.nav.groups.programs, entries: PROGRAM_ENTRIES },
    {
      heading: strings.nav.groups.intelligence,
      entries: [
        { label: strings.nav.lessonsLearned, to: "/lessons" },
        { label: strings.nav.myEvaluations, to: "/evaluations" },
      ],
    },
    {
      heading: strings.nav.groups.workspace,
      entries: [CONFIGURATION, AUDIT, GUIDE, ACCOUNT_ENTRY],
    },
  ];
}

/**
 * The QA/QC engineer (registrar, planner, assessor): the queues, then Projects and Parties
 * (he registers projects, C1.1), the schedules he plans with the planning candidates as
 * their own view (R06: the unplanned work on the calendar), the programs he assesses
 * from (F05/F07/F08), and the non-conformities he raises (F15 originator).
 */
export function plannerNavigation(): NavGroup[] {
  return [
    {
      heading: strings.nav.groups.myWork,
      entries: [
        TASKS_ENTRY,
        { label: strings.nav.queueItpReview, to: QUEUES.itpReview.route },
        { label: strings.nav.queueResultApproval, to: QUEUES.resultApproval.route },
        { label: strings.nav.queueResponses, to: QUEUES.responsesToDecide.route },
        { label: strings.nav.queueRectifications, to: QUEUES.rectifications.route },
      ],
    },
    {
      heading: strings.nav.groups.portfolio,
      entries: [
        { label: strings.nav.projectsContracts, to: "/portfolio/projects" },
        { label: strings.nav.partiesContractors, to: "/portfolio/parties" },
      ],
    },
    {
      heading: strings.nav.groups.planning,
      entries: [
        { label: strings.nav.schedules, to: "/schedules" },
        { label: strings.nav.planningCandidates, to: PLANNING_CANDIDATES_ROUTE },
        { label: strings.nav.calendar, to: "/calendar" },
        { label: strings.nav.rfis, to: "/rfis" },
      ],
    },
    {
      heading: strings.nav.groups.registers,
      entries: [
        { label: strings.nav.itps, to: "/itps" },
        { label: strings.nav.inspectionsRfis, to: "/inspections" },
        { label: strings.nav.findingsPunch, to: "/findings" },
        { label: strings.nav.nonConformities, to: "/actions" },
        { label: strings.nav.standards, to: "/standards" },
        { label: strings.nav.credentials, to: "/credentials" },
      ],
    },
    { heading: strings.nav.groups.programs, entries: PROGRAM_ENTRIES },
    { heading: strings.nav.groups.workspace, entries: [GUIDE, ACCOUNT_ENTRY] },
  ];
}

/** The head of projects QA/QC: the full tree, with his tasks and his signature queue. */
export function managerNavigation(): NavGroup[] {
  const tree = navigationTree();
  return [
    {
      heading: strings.nav.groups.myWork,
      entries: [
        TASKS_ENTRY,
        { label: strings.nav.queueScheduleSign, to: QUEUES.scheduleSign.route },
      ],
    },
    ...tree,
  ];
}

/**
 * Management (ruling 5): status and numbers, no data entry. The read registers, the
 * portfolio (the escalated project approval stays with this class, through its task),
 * the intelligence pages; no Configuration.
 */
export function managementNavigation(): NavGroup[] {
  return [
    { heading: strings.nav.groups.myWork, entries: [TASKS_ENTRY] },
    ...navigationTree()
      .map((group) => ({
        ...group,
        entries: group.entries.filter(
          (entry) => entry.to !== "/configuration" && entry.to !== "/outbox",
        ),
      }))
      .filter((group) => group.entries.length > 0),
  ];
}

/**
 * The sidebar tree for a persona shape. Home and Dashboard are not part of any shape:
 * the sidebar pins them above every tree, so no persona can lose the way back.
 */
export function personaNavigation(shape: PersonaShape): NavGroup[] {
  switch (shape) {
    case "contractor":
      return contractorNavigation();
    case "tpi":
      return tpiNavigation();
    case "inspector":
      return inspectorNavigation();
    case "qcLead":
      return qcLeadNavigation();
    case "planner":
      return plannerNavigation();
    case "manager":
      return managerNavigation();
    case "management":
      return managementNavigation();
  }
}
